<script setup lang="ts">
  import { useFsfStore } from '@/components/fsf/stores/fsf.store'
  import { useFsfHelpers } from '@/components/fsf/composables/useFsfHelpers'
  import { INBOUND, OUTBOUND } from '@/utils/constants/fsf'

  const isCalendarOpen = ref(false)
  const fsfStore = useFsfStore()

  const { showFooterActionBlock } = useFsfHelpers()

  const selectDate = () => {
    fsfStore.currentFlightType = OUTBOUND
    isCalendarOpen.value = true
  }

  const dayjs = useDayjs()

  const VALUE_DATE_FORMAT = 'DD.MM.YYYY'
  const selectedStartDateValue = computed(() =>
    fsfStore.selectedStartDate ? dayjs(fsfStore.selectedStartDate).format(VALUE_DATE_FORMAT) : null
  )
  const selectedEndDateValue = computed(() =>
    fsfStore.selectedEndDate ? dayjs(fsfStore.selectedEndDate).format(VALUE_DATE_FORMAT) : null
  )

  const openInboundTab = () => {
    if (!fsfStore.selectedStartDate) {
      return
    }

    fsfStore.currentFlightType = INBOUND
    isCalendarOpen.value = true
  }

  const isSubmitButtonDisabled = computed(() => {
    if (fsfStore.currentFlightType === INBOUND) {
      return !fsfStore.selectedStartDate || !fsfStore.selectedEndDate
    }

    return !fsfStore.selectedStartDate
  })
</script>

<template>
  <div
    v-if="!isCalendarOpen"
    class="container flex h-full flex-col gap-6 px-4 pb-4"
  >
    <slot name="trip-type" />
    <section
      class="flex flex-col gap-6"
      :aria-label="$t('fsf.flightSearchOptions')"
    >
      <OrigDest
        variant="block"
        watch-query-params
        :show-any-destin-option="false"
      />
      <FsfPaxSelect />
    </section>

    <button
      class="flex h-16 !w-full items-center gap-3 rounded-sm border border-border bg-white px-3 text-left"
      @click="selectDate"
    >
      <Icon name="common/calendar-event" />
      <div class="flex flex-col gap-1">
        <span class="text-secondary-light text-body-2">{{ $t('fsf.outboundDate') }}</span>
        {{ selectedStartDateValue ?? $t('fsf.selectDate') }}
      </div>
    </button>

    <button
      v-if="fsfStore.isRoundTrip"
      class="flex h-16 !w-full items-center gap-3 rounded-sm border border-border bg-white px-3 text-left"
      @click="openInboundTab"
    >
      <Icon name="common/calendar-event" />
      <div class="flex flex-col gap-1">
        <span class="text-secondary-light text-body-2">{{ $t('fsf.inboundDate') }}</span>
        {{ selectedEndDateValue ?? $t('fsf.selectDate') }}
      </div>
    </button>

    <FsfVoucher mobile />

    <UiButton
      variant="primary"
      :title="$t('fsf.search')"
      rounded="medium"
      :disabled="isSubmitButtonDisabled"
      @click="fsfStore.submitForm"
    />
  </div>

  <div v-if="isCalendarOpen">
    <div class="fixed inset-0 z-20 grid grid-rows-[auto_1fr_auto]">
      <div
        class="flex w-full min-w-0 flex-col border-b border-b-border bg-white"
        data-mobile-top-navigation
      >
        <slot name="switch" />
        <div class="flex justify-between gap-4 px-2">
          <button
            class="flex items-center gap-2 bg-white py-4"
            @click="isCalendarOpen = false"
          >
            <Icon name="common/chevron-left" />
            {{ $t('forms.back') }}
          </button>
        </div>
        <div v-if="$slots['tab-row']">
          <slot name="tab-row" />
        </div>
        <div :class="{ 'border-t border-t-divider': !fsfStore.isRoundTrip }">
          <slot name="month-menu" />
        </div>
      </div>
      <div
        class="min-h-0 min-w-0 overflow-y-auto overscroll-contain bg-white pb-12"
        data-mobile-calendar-scroll
      >
        <slot name="calendar" />
      </div>
      <div class="w-full min-w-0 border-t border-t-divider bg-white">
        <template v-if="!showFooterActionBlock || (fsfStore.isOutboundFlightType && fsfStore.isRoundTrip)">
          <slot name="legends" />
        </template>
        <template v-else>
          <div class="w-full grow">
            <slot name="footer" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
